import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const EmailVerification: React.FC = () => {
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [checkingVerification, setCheckingVerification] = useState(false);
  const { currentUser, resendEmailVerification, refreshUserData } = useAuth();
  const navigate = useNavigate();

  // Check if email is verified periodically
  useEffect(() => {
    const checkVerification = async () => {
      if (currentUser && currentUser.emailVerified) {
        // Email is verified, proceed to profile setup
        navigate('/profile-setup');
      }
    };

    const interval = setInterval(checkVerification, 2000); // Check every 2 seconds
    return () => clearInterval(interval);
  }, [currentUser, navigate]);

  const handleResendVerification = async () => {
    try {
      setResendLoading(true);
      setResendMessage('');
      await resendEmailVerification();
      setResendMessage('Verification email sent! Please check your inbox.');
    } catch (error) {
      setResendMessage('Failed to send verification email. Please try again.');
      console.error(error);
    } finally {
      setResendLoading(false);
    }
  };

  const handleRefreshUser = async () => {
    setCheckingVerification(true);
    try {
      await refreshUserData();
      setCheckingVerification(false);
      // The useEffect will handle the navigation when currentUser.emailVerified changes
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setCheckingVerification(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-indigo-600">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Welcome to Mindful Practice Buddy!
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent a verification email to
          </p>
          <p className="mt-1 text-center text-sm font-medium text-indigo-600">
            {currentUser.email}
          </p>
        </div>

        <div className="mt-8 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Check your email
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Hello! To begin your journey with Mindful Practice Buddy, we just need to verify your email address. 
                    Please check your email inbox and click the verification link to activate your account.
                    The email might take a few minutes to arrive.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleRefreshUser}
              disabled={checkingVerification}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {checkingVerification ? 'Checking...' : 'I\'ve verified my email'}
            </button>

            <button
              onClick={handleResendVerification}
              disabled={resendLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {resendLoading ? 'Sending...' : 'Resend verification email'}
            </button>
          </div>

          {resendMessage && (
            <div className={`text-sm text-center ${
              resendMessage.includes('Failed') ? 'text-red-600' : 'text-green-600'
            }`}>
              {resendMessage}
            </div>
          )}

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Didn't receive the email? Check your spam folder or{' '}
              <button
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
              >
                resend it
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
